import React, { useState } from 'react';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AddOutlined from '@mui/icons-material/AddOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DnsOutlined from '@mui/icons-material/DnsOutlined';
import { Card, Badge, Button, IconButton, EmptyState } from '../../../components/ui';
import PasControllerDialog from './PasControllerDialog';

const ControllerList = ({ controllers, areaName, onBack, onSelectController, onControllerChanged, onControllerDeleted }) => {
    const [addingController, setAddingController] = useState(false);
    const [editingController, setEditingController] = useState(null);
    const isLifeLine = (controller) =>
        (controller?.description || controller?.name || '').toLowerCase().includes('life line');
    // Determine which control room type to show based on the area
    const getControlRoomConfig = () => {
        const normalizedAreaName = areaName.toLowerCase();

        // MCR Areas:

        // Concourse A -> MCR A only
        if (normalizedAreaName.includes('concourse a')) {
            return { type: 'MCR', areaKeyA: 'concourse_a', showA: true, showB: false, split: false };
        }

        // Concourse B -> MCR B only
        if (normalizedAreaName.includes('concourse b')) {
            return { type: 'MCR', areaKeyB: 'concourse_b', showA: false, showB: true, split: false };
        }

        // Concourse C South Node -> Both MCR A and MCR B (vertical split)
        if (normalizedAreaName.includes('south node')) {
            // Prefer Concourse C South Node West/East, fall back to original South Node MCR A/B
            return {
                type: 'MCR',
                areaKeyA: 'concourse_c_sn_west',
                areaKeyB: 'concourse_c_sn_east',
                altAreaKeyA: 'south_node_mcr_a',
                altAreaKeyB: 'south_node_mcr_b',
                showA: true,
                showB: true,
                split: true
            };
        }

        // MTB -> Both MCR A and MCR B (vertical split)
        if (normalizedAreaName.includes('mtb')) {
            return { type: 'MCR', areaKeyA: 'mtb_mcr_a', areaKeyB: 'mtb_mcr_b', showA: true, showB: true, split: true };
        }

        // DCR Areas:

        // North Node and Central Concourse share the same DCR.
        if (normalizedAreaName.includes('north node') || normalizedAreaName.includes('central concourse')) {
            return { type: 'DCR', area: 'dcr_north_node', showA: false, showB: false, split: false };
        }

        // Concourse D -> DCR
        if (normalizedAreaName.includes('concourse d')) {
            return { type: 'DCR', area: 'dcr_concourse_d', showA: false, showB: false, split: false };
        }

        // Concourse E -> DCR
        if (normalizedAreaName.includes('concourse e')) {
            return { type: 'DCR', area: 'dcr_concourse_e', showA: false, showB: false, split: false };
        }

        // Default: DCR
        return { type: 'DCR', area: 'dcr_north_node', showA: false, showB: false, split: false };
    };

    // Filter controllers by location code from the passed controllers prop
    const getControllersByLocation = (locationCode, altCode) => {
        if (!controllers || !Array.isArray(controllers)) return [];

        return controllers.filter(c => {
            const code = c.location?.code || c.locationCode;
            return code === locationCode || (!!altCode && code === altCode);
        });
    };

    const config = getControlRoomConfig();
    const mcrAControllers = config.type === 'MCR' && config.showA ? getControllersByLocation(config.areaKeyA, config.altAreaKeyA) : [];
    const mcrBControllers = config.type === 'MCR' && config.showB ? getControllersByLocation(config.areaKeyB, config.altAreaKeyB) : [];
    const dcrControllers = config.type === 'DCR' ? getControllersByLocation(config.area) : [];

    // One card definition for both the split and single layouts — these were two
    // near-identical blocks that had already drifted apart in spacing and hover.
    const ControllerCard = ({ controller }) => {
        const disabled = isLifeLine(controller);
        const online = controller.status === 'active' || controller.status === 'Online';

        return (
            <Card
                interactive={!disabled}
                onClick={disabled ? undefined : () => onSelectController && onSelectController(controller)}
                className={disabled ? 'opacity-70' : ''}
            >
                <div className="flex items-start justify-between gap-3 p-4">
                    <div className="min-w-0">
                        <h4 className="truncate font-semibold text-foreground">
                            {controller.description || controller.name}
                        </h4>

                        <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-fg">
                            <DnsOutlined style={{ fontSize: 16 }} aria-hidden="true" className="shrink-0" />
                            <span className="tabular rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs">
                                {controller.ipAddress || controller.ip || 'N/A'}
                            </span>
                        </div>

                        {disabled && (
                            <p className="mt-2 text-xs text-warning-fg">Life Line — no devices to view</p>
                        )}
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-2">
                        {/* Status carries a text label too, not colour alone. */}
                        <Badge variant={online ? 'success' : 'neutral'} size="sm" dot>
                            {controller.status || (online ? 'Online' : 'Offline')}
                        </Badge>
                        <IconButton
                            icon={EditOutlined}
                            label={`Edit ${controller.name}`}
                            variant="secondary"
                            onClick={(event) => {
                                event.stopPropagation();
                                setEditingController(controller);
                            }}
                        />
                    </div>
                </div>
            </Card>
        );
    };

    const ControllerGrid = ({ controllerList, roomName, columns = true }) => (
        <div className="min-w-0 flex-1">
            {roomName && (
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-fg">
                    {roomName}
                </h3>
            )}

            {controllerList.length === 0 ? (
                <EmptyState
                    icon={DnsOutlined}
                    title="No controllers"
                    description={roomName ? `Nothing found in ${roomName}.` : 'Nothing found in this area.'}
                />
            ) : (
                <div
                    className={
                        columns
                            ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'
                            : 'grid grid-cols-1 gap-4'
                    }
                >
                    {controllerList.map((controller, index) => (
                        <ControllerCard key={controller._id || `${roomName}-${index}`} controller={controller} />
                    ))}
                </div>
            )}
        </div>
    );

    const getTitle = () => {
        if (config.type === 'DCR') return `DCR — ${areaName}`;
        if (config.split) return `Controllers in ${areaName}`;
        return `${config.showA ? 'MCR A' : 'MCR B'} — ${areaName}`;
    };

    return (
        <div className="animate-fade-in">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <IconButton icon={ArrowBack} label="Back to overview" onClick={onBack} variant="secondary" />
                    <h2 className="min-w-0 truncate text-lg font-semibold text-foreground sm:text-xl">
                        {getTitle()}
                    </h2>
                </div>
                <Button icon={AddOutlined} onClick={() => setAddingController(true)}>Add controller</Button>
            </div>

            {/* Split rooms stack on mobile — two columns at 375px would be unreadable. */}
            {config.type === 'MCR' && config.split && (
                <div className="flex flex-col gap-8 lg:flex-row lg:gap-6">
                    {config.showA && <ControllerGrid controllerList={mcrAControllers} roomName="MCR A" columns={false} />}
                    {config.showA && config.showB && (
                        <div className="hidden w-px self-stretch bg-border lg:block" aria-hidden="true" />
                    )}
                    {config.showB && <ControllerGrid controllerList={mcrBControllers} roomName="MCR B" columns={false} />}
                </div>
            )}

            {config.type === 'MCR' && !config.split && config.showA && (
                <ControllerGrid controllerList={mcrAControllers} />
            )}
            {config.type === 'MCR' && !config.split && config.showB && (
                <ControllerGrid controllerList={mcrBControllers} />
            )}

            {config.type === 'DCR' && <ControllerGrid controllerList={dcrControllers} />}

            <PasControllerDialog
                isOpen={addingController}
                createMode
                defaultLocationId={controllers?.[0]?.location?._id || controllers?.[0]?.location || ''}
                onClose={() => setAddingController(false)}
                onChanged={onControllerChanged}
            />

            <PasControllerDialog
                isOpen={Boolean(editingController)}
                controller={editingController}
                onClose={() => setEditingController(null)}
                onChanged={onControllerChanged}
                onDeleted={onControllerDeleted}
            />
        </div>
    );
};

export default ControllerList;
